import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Calendar, Clock, Edit2, Archive, Users, FileText, XCircle, RotateCcw, PlayCircle, BarChart3, Link, EyeOff, Play, Globe } from 'lucide-react';
import NotificationToast from './NotificationToast';
import { getPropertyStatus } from '../utils/propertyHelpers';
import { getPropertyIcon } from '../constants/propertyIcons';
import { getPropertyBlindOffers } from '../services/supabaseService';
import { SemaphoreIcon } from './SemaphoreIndicator';
import { getBlindBiddingEndDate, DEFAULT_SEMAPHORE_THRESHOLDS } from '../config/blindBiddingConfig';

/**
 * Componente para mostrar una tarjeta de propiedad individual
 * ✅ ACTUALIZADO: Soporte para modo archivadas con botón Reactivar
 */
const PropertyCard = ({
  property,
  propertyBids,
  propertyInvites,
  onEdit,
  onArchive,
  onUnarchive,
  onManageInvites,
  onViewConditions,
  onStartLastChance,
  onCloseLastChance,
  onStartBlindBidding,
  onActivateBlindActive,
  onCloseBlindBidding,
  onStartOpenBidding,
  isArchived = false
}) => {
  const [blindOffers, setBlindOffers] = useState([]);
  
  const navigate = useNavigate();
  const highestBid = propertyBids[0];
  const hasBids = propertyBids.length > 0;
  
  const status = getPropertyStatus(property);
  const isUpcoming = status === 'UPCOMING';
  const isOpen = status === 'OPEN';
  const isClosed = status === 'CLOSED';
  const isLastChance = status === 'LAST_CHANCE';
  const isBlindWaiting = status === 'BLIND_WAITING';
  const isBlindActive = status === 'BLIND_ACTIVE';
  const isBlindPhase = isBlindWaiting || isBlindActive;
  
  const hasBlindOffers = blindOffers.length > 0;

  // ✅ SIMPLIFICADO: Mostrar botón de activación si hay ofertas verificadas (sin importar el color)
  const verifiedBlindOffers = blindOffers.filter(offer => offer.isVerified);
  const hasVerifiedOffers = verifiedBlindOffers.length > 0;
  const shouldShowActivateButton = isBlindWaiting && hasVerifiedOffers;

  // Cargar ofertas ciegas siempre que haya fase ciega configurada (activa, expirada o cerrada)
  useEffect(() => {
    const loadBlindOffers = async () => {
      if (property.blind_bidding_deadline) {
        try {
          const offers = await getPropertyBlindOffers(property.id);
          setBlindOffers(offers);
        } catch (error) {
          console.error('Error cargando ofertas ciegas:', error);
        }
      }
    };
    loadBlindOffers();
  }, [property.id, property.blind_bidding_deadline]);
  
  const currentPrice = hasBids ? highestBid.amount : property.startPrice;

  // Estado para notificación de enlace copiado
  const [notification, setNotification] = useState(null);

  // Handler para copiar enlace público (sin token)
  const handleCopyPublicLink = async () => {
    const publicUrl = `${window.location.origin}/property/${property.id}`;

    try {
      await navigator.clipboard.writeText(publicUrl);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = publicUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }

    setNotification({
      type: 'success',
      title: 'Enlace público copiado',
      message: 'Cualquier persona con este enlace verá la vista pública'
    });
    setTimeout(() => setNotification(null), 3000);
  };

  // Handler para copiar enlace de espectador
  const handleCopySpectatorLink = async () => {
    if (!property.spectatorToken) {
      setNotification({
        type: 'error',
        title: 'Error',
        message: 'No se encontró el enlace de espectador'
      });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    const spectatorUrl = `${window.location.origin}/property/${property.id}/spectate/${property.spectatorToken}`;
    
    try {
      await navigator.clipboard.writeText(spectatorUrl);
      setNotification({
        type: 'success',
        title: 'Enlace copiado',
        message: 'Enlace de espectador copiado al portapapeles'
      });
    } catch (err) {
      // Fallback para navegadores que no soportan clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = spectatorUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setNotification({
        type: 'success',
        title: 'Enlace copiado',
        message: 'Enlace de espectador copiado al portapapeles'
      });
    }
    
    setTimeout(() => setNotification(null), 3000);
  };

  // Usar iconos centralizados
  const iconConfig = getPropertyIcon(status);
  const StatusIcon = iconConfig.icon;

  const statusConfig = {
    UPCOMING: {
      text: 'Próximamente',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      textColor: 'text-blue-400'
    },
    BLIND_WAITING: {
      text: 'Oferta Ciega (Esperando)',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      textColor: 'text-purple-400'
    },
    BLIND_ACTIVE: {
      text: 'Oferta Ciega (Activa)',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      textColor: 'text-purple-400'
    },
    OPEN: {
      text: 'Subasta Abierta',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
      textColor: 'text-green-400'
    },
    LAST_CHANCE: {
      text: 'Última Oportunidad',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/30',
      textColor: 'text-amber-400'
    },
    CLOSED: {
      text: 'Finalizado',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      textColor: 'text-red-400'
    }
  };

  const currentStatus = statusConfig[status];

  return (
    <div className={`bg-slate-800/50 backdrop-blur-xl rounded-xl border overflow-hidden ${
      isArchived ? 'border-slate-600/30 opacity-75' : 'border-slate-700/50'
    }`}>
      <div className="p-6">
        {/* Header con título, foto y acciones */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex gap-4 flex-1">
            {/* Thumbnail */}
            {property.photoUrls && property.photoUrls.length > 0 && (
              <div className="flex-shrink-0">
                <img 
                  src={property.photoUrls[0]} 
                  alt={property.title}
                  className={`w-24 h-24 object-cover rounded-lg border-2 ${
                    isArchived ? 'border-slate-600 grayscale' : 'border-slate-700'
                  }`}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          
            {/* Info */}
            <div className="flex-1">
              {/* Badges de estado - Orden: Estado primero, Archivada después */}
              <div className="mb-2 flex items-center gap-2 flex-wrap">
                {/* Badge de estado normal */}
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${currentStatus.bgColor} border ${currentStatus.borderColor} rounded-full text-xs font-medium ${currentStatus.textColor}`}>
                  <StatusIcon className="w-3 h-3" />
                  {currentStatus.text}
                </span>
                
                {/* Badge de archivada - después del estado */}
                {isArchived && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-600/30 border border-slate-500/30 rounded-full text-xs font-medium text-slate-400">
                    <Archive className="w-3 h-3" />
                    Archivada
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-16 mb-2">
                <h3 className="text-xl font-bold text-white mb-2">{property.title}</h3>
                
                {/* Mensaje de archivada - centrado debajo del título */}
                {isArchived && (
                  <span  className="text-slate-400 text-sm mb-2 ml-6">
                    Esta propiedad está archivada. Reactívala para gestionar invitados.
                  </span>
                )}
              </div>
              
              <div className="flex flex-wrap gap-4 text-sm">
                {/* Precio inicial (original) */}
                <div className="flex items-center gap-2 text-slate-300">
                  <TrendingUp className="w-4 h-4 text-amber-400" />
                  <span className="text-sm">
                    Precio inicial: <span className="font-semibold">{(property.originalStartPrice || property.startPrice)?.toLocaleString()}€</span>
                  </span>
                </div>

                {/* Invitados */}
                <div className="flex items-center gap-2 text-slate-300">
                  <Users className="w-4 h-4 text-blue-400" />
                  <span className="text-sm">
                    Invitados: <span className="font-semibold">{propertyInvites.length}</span>
                  </span>
                </div>

                {/* Cierre de ofertas */}
                <div className="flex items-center gap-2 text-slate-300">
                  <Calendar className="w-4 h-4 text-purple-400" />
                  <span className="text-sm">
                    Cierre de ofertas: <span className="font-semibold">
                      {(() => {
                        // Si se cerró manualmente, mostrar esa fecha
                        if (property.blind_bidding_closed_at) {
                          const d = new Date(property.blind_bidding_closed_at);
                          return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
                        }
                        // Si hay periodo activo, calcular fecha de fin
                        const endDate = getBlindBiddingEndDate(property);
                        if (endDate) {
                          return endDate.toLocaleDateString();
                        }
                        return '-';
                      })()}
                    </span>
                  </span>
                </div>

                {/* Cierre de subasta abierta */}
                <div className="flex items-center gap-2 text-slate-300">
                  <Calendar className="w-4 h-4 text-red-400" />
                  <span className="text-sm">
                    Cierre de subasta abierta: <span className="font-semibold">
                      {property.deadline
                        ? (() => {
                            const d = new Date(property.deadline);
                            return isNaN(d.getTime()) || d.getFullYear() === 1970
                              ? '-'
                              : d.toLocaleDateString();
                          })()
                        : '-'}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Botones de acción */}
          <div className="flex gap-2">
            {isArchived ? (
              /* ✅ MODO ARCHIVADAS: Botón Analytics + Reactivar */
              <>
                <button
                  onClick={() => navigate(`/admin/engagement/${property.id}?token=${property.spectatorToken}?from=archived`)}
                  className="p-2 bg-indigo-500/10 text-indigo-400 rounded-lg hover:bg-indigo-500/20 transition-all"
                  title="Ver analíticas"
                >
                  <BarChart3 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => onUnarchive && onUnarchive(property.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-all border border-emerald-500/30"
                  title="Reactivar propiedad"
                >
                  <RotateCcw className="w-5 h-5" />
                  <span className="font-medium">Reactivar</span>
                </button>
              </>
            ) : (
              /* ✅ MODO ACTIVAS: Botones normales */
              <>
                {/* Copiar enlace público */}
                <button
                  onClick={handleCopyPublicLink}
                  className="p-2 bg-emerald-500/10 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-all"
                  title="Copiar enlace público (vista sin token)"
                >
                  <Globe className="w-5 h-5" />
                </button>

                {/* Copiar enlace espectador */}
                <button
                  onClick={handleCopySpectatorLink}
                  className="p-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-all"
                  title="Copiar enlace de espectador"
                >
                  <Link className="w-5 h-5" />
                </button>

                {/* Analytics */}
                <button
                  onClick={() => navigate(`/admin/engagement/${property.id}?token=${property.spectatorToken}`)}
                  className="p-2 bg-indigo-500/10 text-indigo-400 rounded-lg hover:bg-indigo-500/20 transition-all"
                  title="Ver analíticas"
                >
                  <BarChart3 className="w-5 h-5" />
                </button>

                {/* Editar */}
                <button
                  onClick={() => onEdit && onEdit(property)}
                  className="p-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-all"
                  title="Editar propiedad"
                >
                  <Edit2 className="w-5 h-5" />
                </button>
                
                {/* Iniciar oferta ciega (solo en UPCOMING) */}
                {isUpcoming && (
                  <button
                    onClick={() => onStartBlindBidding && onStartBlindBidding(property)}
                    className="p-2 bg-purple-500/10 text-purple-400 rounded-lg hover:bg-purple-500/20 transition-all"
                    title="Iniciar fase de oferta ciega"
                  >
                    <EyeOff className="w-5 h-5" />
                  </button>
                )}

                {/* ✅ SIMPLIFICADO: Activar fase activa manualmente (BLIND_WAITING con ofertas verificadas) */}
                {shouldShowActivateButton && (
                  <button
                    onClick={() => onActivateBlindActive && onActivateBlindActive(property, blindOffers)}
                    className="p-2 bg-purple-500/10 text-purple-400 rounded-lg hover:bg-purple-500/20 transition-all"
                    title="Activar fase activa (hay ofertas verificadas)"
                  >
                    <Play className="w-5 h-5" />
                  </button>
                )}

                {/* Cerrar fase ciega (solo en BLIND_ACTIVE) - YA NO selecciona finalistas */}
                {isBlindActive && (
                  <button
                    onClick={() => onCloseBlindBidding && onCloseBlindBidding(property)}
                    className="p-2 bg-purple-500/10 text-purple-400 rounded-lg hover:bg-purple-500/20 transition-all"
                    title="Cerrar fase de oferta ciega"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                )}

                {/* ✅ NUEVO: Iniciar subasta abierta desde CLOSED (después de cerrar fase ciega) */}
                {(() => {
                  // Mostrar botón si cerró blind (manual o por tiempo) Y no se ha configurado subasta abierta después
                  if (!isClosed) return null;

                  // Cierre manual → blind_bidding_closed_at; cierre automático por tiempo → blind_bidding_deadline
                  const blindClosedAt = property.blind_bidding_closed_at
                    ? new Date(property.blind_bidding_closed_at)
                    : (property.blind_bidding_deadline ? new Date(property.blind_bidding_deadline) : null);

                  if (!blindClosedAt) return null;

                  const biddingStart = property.biddingStartDate ? new Date(property.biddingStartDate) : null;

                  // Si no hay biddingStartDate o es anterior al cierre de blind, mostrar botón
                  const needsOpenConfig = !biddingStart || biddingStart <= blindClosedAt;

                  return needsOpenConfig && (
                    <button
                      onClick={() => onStartOpenBidding && onStartOpenBidding(property, blindOffers)}
                      className="p-2 bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-all"
                      title="Abrir subasta abierta"
                    >
                      <Play className="w-5 h-5" />
                    </button>
                  );
                })()}

                {/* ✅ Iniciar última oportunidad (solo si hubo subasta abierta Y ya finalizó) */}
                {(() => {
                  // Mostrar botón si hubo subasta abierta (biddingStartDate posterior a cierre de blind)
                  if (!isClosed) return null;

                  const now = new Date();
                  // Cierre manual → blind_bidding_closed_at; cierre automático por tiempo → blind_bidding_deadline
                  const blindClosedAt = property.blind_bidding_closed_at
                    ? new Date(property.blind_bidding_closed_at)
                    : (property.blind_bidding_deadline ? new Date(property.blind_bidding_deadline) : null);

                  if (!blindClosedAt) return null;
                  const biddingStart = property.biddingStartDate ? new Date(property.biddingStartDate) : null;
                  const deadline = property.deadline ? new Date(property.deadline) : null;

                  // Si biddingStartDate es posterior al cierre de blind, hubo subasta abierta
                  const hadOpenBidding = biddingStart && biddingStart > blindClosedAt;

                  // Solo mostrar si el deadline ya pasó (subasta abierta realmente cerrada)
                  const openBiddingClosed = deadline && deadline < now;

                  return hadOpenBidding && openBiddingClosed && !property.last_chance_started_at && !property.last_chance_closed_at && (
                    <button
                      onClick={() => onStartLastChance && onStartLastChance(property)}
                      className="p-2 bg-emerald-500/10 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-all"
                      title="Iniciar última oportunidad"
                    >
                      <PlayCircle className="w-5 h-5" />
                    </button>
                  );
                })()}

                {/* Cerrar última oportunidad (solo visible en LAST_CHANCE) */}
                {isLastChance && (
                  <button
                    onClick={() => onCloseLastChance && onCloseLastChance(property)}
                    className="p-2 bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-all"
                    title="Cerrar última oportunidad manualmente"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                )}
                
                {/* Archivar */}
                <button
                  onClick={() => onArchive && onArchive(property)}
                  className="p-2 bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-all"
                  title="Archivar propiedad"
                >
                  <Archive className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Encabezado de ofertas ciegas + semáforo: visible en fase blind aunque no haya ofertas aún */}
        {(isBlindPhase || hasBlindOffers) && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-slate-400">
                Ofertas Ciegas ({blindOffers.length})
              </h4>
              {(() => {
                const sp = property.originalStartPrice || property.startPrice;
                const g = property.semaphore_green_threshold ?? DEFAULT_SEMAPHORE_THRESHOLDS.green;
                const y = property.semaphore_yellow_threshold ?? DEFAULT_SEMAPHORE_THRESHOLDS.yellow;
                return (
                  <span className="text-xs text-slate-500 flex items-center gap-2">
                    <span className="text-green-400">🟢 ≥{sp ? Math.round(sp * (1 - g/100)).toLocaleString('es-ES') + '€' : `${100-g}%`}</span>
                    <span className="text-yellow-400">🟡 {sp ? `${Math.round(sp * (1 - y/100)).toLocaleString('es-ES')}€–${Math.round(sp * (1 - g/100)).toLocaleString('es-ES')}€` : `${100-y}%–${100-g}%`}</span>
                    <span className="text-red-400">🔴 &lt;{sp ? Math.round(sp * (1 - y/100)).toLocaleString('es-ES') + '€' : `${100-y}%`}</span>
                  </span>
                );
              })()}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {blindOffers.slice(0, 6).map((offer, index) => (
                <div key={offer.id} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-2">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <SemaphoreIcon status={offer.semaphoreStatus} size="md" />
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0 ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-300'
                      }`}>
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{offer.buyerName}</span>
                        {!offer.isVerified && (
                          <span className="text-xs text-amber-400">(pendiente)</span>
                        )}
                        <span className="text-slate-400 text-xs">{new Date(offer.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="text-lg font-bold text-white">{offer.amount.toLocaleString()}€</div>

                    {offer.conditionsText && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewConditions && onViewConditions({
                            ...offer,
                            buyerEmail: offer.buyerEmail,
                            conditionsText: offer.conditionsText,
                            selectedConditions: []
                          });
                        }}
                        className="p-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-all"
                        title="Ver condiciones"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lista de ofertas abiertas (OPEN / LAST_CHANCE) */}
        {hasBids && !isBlindPhase && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-slate-400 mb-2">Ofertas en Subasta Abierta ({propertyBids.length})</h4>
            <div className="grid grid-cols-2 gap-3">
              {propertyBids.slice(0, 6).map((bid, index) => (
                <div key={bid.id} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0 ? 'bg-amber-500 text-slate-900' : 'bg-slate-700 text-slate-300'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{bid.buyerName}</span>

                        {/* Badge de última oportunidad */}
                        {bid.is_last_chance && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-xs font-medium text-amber-400">
                            <Clock className="w-3 h-3" />
                            Última oportunidad
                          </span>
                        )}

                        <span className="text-slate-400 text-xs">{new Date(bid.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="text-lg font-bold text-white">{bid.amount.toLocaleString()}€</div>

                    {/* Botón para ver condiciones */}
                    {bid.hasConditions && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewConditions && onViewConditions(bid);
                        }}
                        className="p-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-all"
                        title="Ver condiciones"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Botón gestionar interesados - Solo en modo activas */}
        {!isArchived && (
          <button
            onClick={() => onManageInvites && onManageInvites(property)}
            className="w-full flex items-center justify-center gap-2 bg-slate-700/50 text-white font-medium px-4 py-3 rounded-lg hover:bg-slate-700 transition-all"
          >
            <Users className="w-4 h-4" />
            Gestionar Interesados
          </button>
        )}

        {/* Toast de notificación para enlace copiado */}
        {notification && (
          <NotificationToast
            type={notification.type}
            title={notification.title}
            message={notification.message}
            onClose={() => setNotification(null)}
          />
        )}
      </div>
    </div>
  );
};

export default PropertyCard;